import { useId } from "react";
import { Trans, useLingui } from "@lingui/react/macro";
import clsx from "clsx";
import { Heading } from "@medoma/components/Heading/Heading";
import { Text } from "@medoma/components/Text/Text";
import CrossIcon from "@medoma/components/icons/CrossIcon";
import styles from "./FilterBanner.module.scss";
import { PlainButton } from "@medoma/components/Button/Button";

export const FilterBanner = ({
  filters,
  onClearAllFilters,
  className,
  ...props
}: {
  filters: Array<
    | {
        name: string;
        value: string;
        onClear: () => void;
      }
    | undefined
  >;
  onClearAllFilters: () => void;
  className?: string;
}) => {
  const { t } = useLingui();
  const headingId = useId();
  const activeFilters = filters.filter((filter) => filter !== undefined);
  const hasActiveFilters = activeFilters.length > 0;

  return (
    <section
      {...props}
      className={clsx(
        !hasActiveFilters && styles.sectionHidden,
        styles.filterBanner,
        className,
      )}
      aria-labelledby={headingId}
    >
      <Heading level="h2" size="small" weight="medium" id={headingId}>
        <Trans>Filter</Trans>:
      </Heading>
      {/* oxlint-disable-next-line jsx-a11y/prefer-tag-over-role -- The status region contains a semantic list, which is not valid inside output. */}
      <div role="status" aria-live="polite">
        {hasActiveFilters ? (
          <ul className={styles.filterTags}>
            {activeFilters.map((filter) => {
              const lowercaseFilterName = filter.name.toLowerCase();
              return (
                <li key={filter.name}>
                  <button
                    type="button"
                    className={styles.filterTag}
                    onClick={filter.onClear}
                    aria-label={t`Rensa ${lowercaseFilterName}`}
                  >
                    <Text
                      element="span"
                      size="smallest"
                      weight="medium"
                      color="inherit"
                    >
                      <span className={styles.filterTagName}>
                        {filter.name}:
                      </span>{" "}
                      {filter.value}
                    </Text>
                    <span className={styles.filterTagIcon} aria-hidden>
                      <CrossIcon />
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        ) : (
          <Text element="span" size="small">
            <Trans>Inga aktiva filter</Trans>
          </Text>
        )}
      </div>
      {hasActiveFilters && (
        <PlainButton
          size="small"
          weight="light"
          color="primary"
          onClick={onClearAllFilters}
        >
          <Trans>Rensa alla filter</Trans>
        </PlainButton>
      )}
    </section>
  );
};
